<!--Message.vue-->
<script setup>
import { onMounted, computed, ref, toRefs, toRef } from 'vue';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useTrack } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import AiToolCallBubble from './bubbles/AiToolCall.vue';
import ImageBubble from './bubbles/Image.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import Forward from './bubbles/Forward.vue';
import InputSelect from './bubbles/InputSelect.vue';
import CtaUrlBubble from './bubbles/CtaUrl.vue';
import OrderDetailsBubble from './bubbles/OrderDetails.vue';
import CallBubble from './bubbles/Call.vue';
import VoiceTranscriptBubble from './bubbles/VoiceTranscript.vue';
import BaileysInteractiveBubble from './bubbles/BaileysInteractive.vue';

import MessageError from './MessageError.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true },
  currentUserId: { type: Number, required: true },
  groupWithNext: { type: Boolean, default: false },
  groupWithPrevious: { type: Boolean, default: false },
  inboxId: { type: Number, default: null },
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null },
  isEmailInbox: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' },
  // Webchat de continuidade (coluna `messages.webchat_kind`, camelizada pelo
  // MessageList): 'standard' | 'screen' (trafegou pela tela do webchat, nos
  // dois sentidos — nunca tem source_id da Meta) | 'link' (o convite/reenvio
  // do link, mensagem paga no WhatsApp). Ausente = mensagem comum.
  webchatKind: { type: String, default: 'standard' },
  additionalAttributes: { type: Object, default: () => ({}) },
  forwardedMessage: { type: Object, default: () => ({}) },
  isSelectionMode: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
  isGroupConversation: { type: Boolean, default: false },
});

const emit = defineEmits(['toggleSelectionMode', 'mounted', 'selectMessage']);
const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const { t } = useI18n();
const route = useRoute();
const store = useStore();

const currentInbox = computed(() => {
  return store.getters['inboxes/getInbox'](props.inboxId) || {};
});

const isBaileysInbox = computed(() => {
  return currentInbox.value.provider === 'baileys';
});

// ✅ NOVO: isGroupConversation já vem como prop

const isAiToolCall = computed(
  () => props.messageType === MESSAGE_TYPES.AI_TOOL_CALL
);

const variant = computed(() => {
  if (isAiToolCall.value) return MESSAGE_VARIANTS.AI_TOOL_CALL;
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  // Call messages
  if (
    [MESSAGE_TYPES.INCOMING_CALL, MESSAGE_TYPES.OUTGOING_CALL].includes(
      props.messageType
    )
  ) {
    return MESSAGE_VARIANTS.CALL;
  }

  // Voice transcript messages
  if (
    props.contentType === CONTENT_TYPES.VOICE_TRANSCRIPT ||
    props.contentAttributes?.voice_transcript
  ) {
    return MESSAGE_VARIANTS.VOICE;
  }

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  // ✅ Mensagem enviada pelo celular (WhatsApp direto) - antes do BOT check
  const sentFromPhone =
    props.additionalAttributes?.sentFromPhone ||
    props.additionalAttributes?.sent_from_phone;
  if (sentFromPhone && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.PHONE;
  }

  const isBot = !props.sender || props.sender.type === SENDER_TYPES.AGENT_BOT;
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

const orientation = computed(() => {
  // AI tool call sempre justify-start (igual playground)
  if (isAiToolCall.value) return ORIENTATION.LEFT;

  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-[1fr_24px]',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: `
      "bubble"
      "meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble avatar"
      "meta spacer"
    `,
  };

  return map[orientation.value];
});

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (isAiToolCall.value) return false;
  if (orientation.value === ORIENTATION.LEFT) return false;

  return true;
});

const isForwarded = computed(() => {
  return props.contentAttributes?.to_forward === true;
});

const componentToRender = computed(() => {
  // AI tool call → pílula violeta (igual playground)
  if (isAiToolCall.value) return AiToolCallBubble;

  // Call messages
  if (
    [MESSAGE_TYPES.INCOMING_CALL, MESSAGE_TYPES.OUTGOING_CALL].includes(
      props.messageType
    )
  ) {
    return CallBubble;
  }

  // Voice transcript messages
  if (
    props.contentType === CONTENT_TYPES.VOICE_TRANSCRIPT ||
    props.contentAttributes?.voice_transcript
  ) {
    return VoiceTranscriptBubble;
  }

  if (isForwarded.value) {
    return Forward;
  }

  // Order Details (PIX/Boleto)
  if (
    props.contentType === 'input_select' &&
    props.contentAttributes?.action?.name === 'order_details'
  ) {
    return OrderDetailsBubble;
  }

  // Botão de link (cta_url) — inclusive o convite do webchat
  if (
    props.contentType === 'input_select' &&
    props.contentAttributes?.action?.name === 'cta_url'
  ) {
    return CtaUrlBubble;
  }

  // ✅ PRIORIDADE: Detectar input_select com items ANTES de tudo
  if (
    props.contentType === 'input_select' &&
    props.contentAttributes?.items?.length > 0
  ) {
    return InputSelect;
  }

  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  if (props.contentAttributes.imageType === 'story_mention') {
    return InstagramStoryBubble;
  }

  // Baileys interactive messages (buttons + optional media/title/footer)
  if (
    Array.isArray(props.contentAttributes?.buttons) &&
    props.contentAttributes.buttons.length > 0
  ) {
    return BaileysInteractiveBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return ![MESSAGE_TYPES.ACTIVITY, MESSAGE_TYPES.AI_TOOL_CALL].includes(
    props.messageType
  );
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
  };
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  return {
    copy: hasText,
    delete:
      isBaileysInbox.value &&
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    forward: hasText || hasAttachments,
    select: props.isSelectionMode,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
  };
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isCallMessage = [
    MESSAGE_TYPES.INCOMING_CALL,
    MESSAGE_TYPES.OUTGOING_CALL,
  ].includes(props.messageType);

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isCallMessage
  );
});

function openContextMenu(e) {
  const shouldSkipContextMenu =
    e.target?.classList.contains('skip-context-menu') ||
    ['a', 'img'].includes(e.target?.tagName.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

function handleToggleSelectionMode() {
  emit('toggleSelectionMode', props);
}

function handleContextMenuMounted() {
  emit('mounted');
}

function handleSelectMessage() {
  emit('selectMessage', props);
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

// ✅ Verifica se mensagem foi enviada pelo celular (WhatsApp direto)
const isSentFromPhone = computed(() => {
  return (
    props.additionalAttributes?.sentFromPhone ||
    props.additionalAttributes?.sent_from_phone
  );
});

const isVoiceTranscript = computed(() => {
  return (
    props.contentType === CONTENT_TYPES.VOICE_TRANSCRIPT ||
    !!props.contentAttributes?.voice_transcript
  );
});

const avatarInfo = computed(() => {
  // Voice transcript da IA — avatar com ícone headset
  if (isVoiceTranscript.value && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return {
      name: 'Voice IA',
      src: '',
      iconName: 'i-lucide-headset',
    };
  }

  // ✅ Mensagem enviada pelo celular (WhatsApp direto, não pelo Chatwoot)
  // Nome com 15 caracteres para gerar cor teal/verde (índice 3 no array de cores)
  if (isSentFromPhone.value) {
    return {
      name: 'Via App Celular', // 15 chars = teal color
      src: '',
      iconName: 'i-lucide-smartphone',
    };
  }

  if (!props.sender) {
    return {
      name: t('CONVERSATION.BOT'),
      src: '',
    };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

provideMessageContext({
  ...toRefs(props),
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
  isGroupConversation: toRef(props, 'isGroupConversation'),
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full message-bubble-container mb-2"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      {
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
      },
    ]"
  >
    <input
      v-if="isSelectionMode && isBubble && !contentAttributes.deleted"
      type="checkbox"
      :checked="isSelected"
      class="message-checkbox"
      @change="handleSelectMessage"
    />

    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <div v-else-if="variant === MESSAGE_VARIANTS.AI_TOOL_CALL">
      <AiToolCallBubble />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="!shouldGroupWithNext && shouldShowAvatar"
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] flex items-end"
      >
        <Avatar v-bind="avatarInfo" :size="24" />
      </div>
      <div
        class="[grid-area:bubble] flex"
        :class="{
          'ltr:ml-8 rtl:mr-8 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-8 rtl:ml-8': orientation === ORIENTATION.LEFT,
          'min-w-0': variant === MESSAGE_VARIANTS.EMAIL,
        }"
        @contextmenu="openContextMenu($event)"
      >
        <Component :is="componentToRender" v-bind="$props" />
      </div>
      <MessageError
        v-if="contentAttributes.externalError"
        class="[grid-area:meta]"
        :class="flexOrientationClass"
        :error="contentAttributes.externalError"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        hide-button
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
        @toggle-selection-mode="handleToggleSelectionMode"
        @mounted="handleContextMenuMounted"
      />
    </div>
  </div>
</template>

<style lang="scss">
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-sm rtl:rounded-tr-sm;
  }

  .right-bubble {
    @apply ltr:rounded-tr-sm rtl:rounded-tl-sm;
  }
}

.message-bubble-container {
  position: relative;
  padding-left: 40px;
}

.message-checkbox {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  margin: 0;
  z-index: 10;
}
</style>
